import { formatShortDate, toDateOnly } from '@odo/shared-ui';
import {
    VIRTUAL_APPOINTMENT_STATUS_LABELS,
    type VirtualAppointmentSummary
} from '@odo/shared-ui/clinical/virtualAppointments/summary';
import type { VirtualAppointmentDto } from '../../services/virtualAppointments';
import { draftWeeksAnchorDay, type VirtualAppointmentDraft } from './virtualAppointmentDraft';
import {
    parseAlignerInput,
    parseNogosInput,
    parseWeeksInput
} from './virtualAppointmentTriggerInput';
import { closedAt } from './openVirtualAppointments';
import { NO_SAVED_TRIGGERS, type SavedTriggers } from './virtualAppointmentTriggerAhead';
import { todayUtcIso, utcDayOf, weeksCountStart, weeksDueDate } from './virtualAppointmentWeeks';

const UNTYPED_TITLE = 'Untitled';

export type VirtualAppointmentTypeLabels = ReadonlyMap<string, string>;

function distinctLabel(code: string | null, label: string | null | undefined): string | undefined {
    return code && label && label !== code ? label : undefined;
}

function labelField(label: string | undefined): { typeLabel?: string } {
    return label === undefined ? {} : { typeLabel: label };
}

// Any VA-shaped record carries these, including the cross-patient list items.
export type VirtualAppointmentSummarySource = Pick<
    VirtualAppointmentDto,
    | 'id'
    | 'typeCode'
    | 'typeCustom'
    | 'typeLabel'
    | 'triggerKind'
    | 'triggerAligner'
    | 'triggerWeeks'
    | 'triggerWeeksFrom'
    | 'triggerNogos'
    | 'nogoCount'
    | 'team'
    | 'status'
    | 'description'
> & {
    /** Already resolved by the API (the cross-patient list); wins over `treatmentStartDate`. */
    dueDate?: string | null;
};

function sourceDueDate(
    dto: VirtualAppointmentSummarySource,
    treatmentStartDate: string | null
): string | null {
    if (dto.triggerKind !== 'weeks') return null;
    if (dto.dueDate !== undefined) return dto.dueDate;
    return weeksDueDate(
        weeksCountStart(treatmentStartDate, dto.triggerWeeksFrom),
        dto.triggerWeeks
    );
}

export function summaryFromDto(
    dto: VirtualAppointmentSummarySource,
    treatmentStartDate: string | null = null
): VirtualAppointmentSummary {
    return {
        key: dto.id,
        typeTitle: dto.typeCode || dto.typeCustom?.trim() || UNTYPED_TITLE,
        ...labelField(distinctLabel(dto.typeCode, dto.typeLabel)),
        triggerKind: dto.triggerKind,
        triggerAligner: dto.triggerAligner,
        triggerWeeks: dto.triggerWeeks,
        weeksAnchorDay: dto.triggerKind === 'weeks' ? utcDayOf(dto.triggerWeeksFrom) : null,
        dueDate: sourceDueDate(dto, treatmentStartDate),
        triggerNogos: dto.triggerNogos,
        nogoCount: dto.nogoCount,
        team: dto.team,
        status: dto.status,
        description: dto.description
    };
}

function draftTypeTitle(draft: VirtualAppointmentDraft): string {
    const title = draft.typeMode === 'custom' ? draft.typeCustom.trim() : draft.typeCode;
    return title || UNTYPED_TITLE;
}

function draftTypeLabel(
    draft: VirtualAppointmentDraft,
    labels: VirtualAppointmentTypeLabels
): string | undefined {
    if (draft.typeMode === 'custom' || draft.typeCode === '') return undefined;
    return distinctLabel(draft.typeCode, labels.get(draft.typeCode));
}

export interface DraftSummaryContext {
    treatmentStartDate?: string | null;
    /** Saved triggers by VA id, so an unchanged "from now" week keeps its saved anchor. */
    savedTriggers?: SavedTriggers;
}

export function summaryFromDraft(
    draft: VirtualAppointmentDraft,
    labels: VirtualAppointmentTypeLabels = new Map(),
    { treatmentStartDate = null, savedTriggers = NO_SAVED_TRIGGERS }: DraftSummaryContext = {}
): VirtualAppointmentSummary {
    const triggerWeeks = parseWeeksInput(draft.triggerWeeks);
    const weeksAnchorDay = draftWeeksAnchorDay(draft, savedTriggers, todayUtcIso());
    return {
        key: draft.key,
        typeTitle: draftTypeTitle(draft),
        ...labelField(draftTypeLabel(draft, labels)),
        triggerKind: draft.triggerKind,
        triggerAligner: parseAlignerInput(draft.triggerAligner),
        triggerWeeks,
        weeksAnchorDay,
        dueDate:
            draft.triggerKind === 'weeks'
                ? weeksDueDate(weeksAnchorDay ?? treatmentStartDate, triggerWeeks)
                : null,
        triggerNogos: parseNogosInput(draft.triggerNogos),
        nogoCount: null,
        team: draft.team,
        status: 'planned',
        description: null
    };
}

// "Done Oct 5, 2026" / "Canceled Oct 5, 2026" for a closed VA; undefined while it is live.
export function formatClosedLabel(va: VirtualAppointmentDto): string | undefined {
    if (va.status !== 'done' && va.status !== 'canceled') return undefined;
    const date = new Date(closedAt(va));
    if (Number.isNaN(date.getTime())) return undefined;
    return `${VIRTUAL_APPOINTMENT_STATUS_LABELS[va.status]} ${formatShortDate(toDateOnly(date))}`;
}

// Puts one row (e.g. a VA just converted from a legacy note) first; everything else keeps its order.
export function summariesWithKeyFirst(
    summaries: VirtualAppointmentSummary[],
    firstKey: string | null
): VirtualAppointmentSummary[] {
    const first = firstKey === null ? undefined : summaries.find((s) => s.key === firstKey);
    return first ? [first, ...summaries.filter((s) => s !== first)] : summaries;
}
